import React, { useState } from 'react';
import { getMonthMatrix, addMonths, formatDateKey, isSameDay, isInRange } from './dateUtil';
import './FlightsCalendar.css';
export default function CalendarPriceView({ priceData, minDate = new Date(), maxDate }) {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [range, setRange] = useState({ start: null, end: null });
    const [hoverDate, setHoverDate] = useState(null);

    const handleDayClick = (date) => {
        if (date < minDate || (maxDate && date > maxDate)) return;

        if (!range.start || (range.start && range.end)) {
            setRange({ start: date, end: null });
        } else if (date >= range.start) {
            setRange({ ...range, end: date });
        } else {
            setRange({ start: date, end: null });
        }
    };

    const canGoPrev = () => {
        const prevMonth = addMonths(currentMonth, -1);
        return prevMonth >= new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    };

    const canGoNext = () => {
        if (!maxDate) return true;
        const nextMonth = addMonths(currentMonth, 1);
        return nextMonth <= new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
    };

    const handlePrev = () => canGoPrev() && setCurrentMonth(addMonths(currentMonth, -1));
    const handleNext = () => canGoNext() && setCurrentMonth(addMonths(currentMonth, 1));
    const handleToday = () => {
        const today = new Date();
        if (today >= minDate && (!maxDate || today <= maxDate)) {
            setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
        }
    };

    const monthsToShow = [0, 1].map(offset => addMonths(currentMonth, offset));

    return (
        <div className="calendar-container">
            <div className="calendar-controls">
                <button onClick={handlePrev} disabled={!canGoPrev()}>‹</button>
                <span>{currentMonth.toLocaleDateString('default', { month: 'long', year: 'numeric' })}</span>
                <button onClick={handleToday}>Today</button>
                <button onClick={handleNext} disabled={!canGoNext()}>›</button>
            </div>

            <div className="calendar-months">
                {monthsToShow.map(month => (
                    <MonthView
                        key={month}
                        monthDate={month}
                        priceData={priceData}
                        onDayClick={handleDayClick}
                        range={range}
                        minDate={minDate}
                        maxDate={maxDate}
                        hoverDate={hoverDate}
                        setHoverDate={setHoverDate}
                    />
                ))}
            </div>
        </div>
    );
}

function MonthView({ monthDate, priceData, onDayClick, range, minDate, maxDate,
    hoverDate, setHoverDate
}) {
    const matrix = getMonthMatrix(monthDate);


    return (
        <div className="month">
            <div className="month-label">
                {monthDate.toLocaleDateString('default', { month: 'long', year: 'numeric' })}
            </div>
            <div className="weekdays">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(d => <div key={d}>{d}</div>)}
            </div>
            <div className="days-grid">
                {matrix.map((week, i) => (
                    <React.Fragment key={i}>
                        {week.map((day, j) => (
                            <DayCell
                                key={j}
                                date={day}
                                priceData={priceData}
                                onClick={onDayClick}
                                range={range}
                                minDate={minDate}
                                maxDate={maxDate}
                                hoverDate={hoverDate}
                                setHoverDate={setHoverDate}
                            />
                        ))}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}

function DayCell({ date, priceData, onClick, range, minDate, maxDate, hoverDate, setHoverDate }) {
    if (!date) return <div className="empty-day" />;
    const inSelectedRange = range.start && range.end && isInRange(date, range.start, range.end);
    const inHoverRange = range.start && !range.end && hoverDate && isInRange(date, range.start, hoverDate);

    const dateKey = formatDateKey(date);
    const entry = priceData[dateKey] || {};
    const { price, delta } = entry;

    let colorClass = '';
    if (delta < -50) colorClass = 'green';
    else if (delta > 50) colorClass = 'red';
    else if (price) colorClass = 'orange';

    const isStart = range.start && isSameDay(date, range.start);
    const isEnd = range.end && isSameDay(date, range.end);
    const inRange = range.start && range.end && isInRange(date, range.start, range.end);
    const isDisabled = date < minDate || (maxDate && date > maxDate);

    return (
        <div
            className={`day-cell ${colorClass} ${isStart ? 'start' : ''} ${isEnd ? 'end' : ''} ${inSelectedRange ? 'in-range' : ''} ${inHoverRange ? 'hover-range' : ''} ${isDisabled ? 'disabled' : ''}`}
            onClick={() => !isDisabled && onClick(date)}
            onMouseEnter={() => !isDisabled && setHoverDate(date)}
        >
            <div className="date">{date.getDate()}</div>
            {price && <div className="price">${price}</div>}
            {delta !== undefined && <div className="delta">{delta > 0 ? `+${delta}` : delta}</div>}
        </div>
    );
}